// Head/body custom-code tag support for /gp/ AI-generated pages.
//
// Unlike customHeadScript/customBodyScript (components/templates-generator/config/index.js — raw
// JS injected as a <script> for regular Generator pages), headCustomCode/bodyCustomCode are
// restricted to bare <meta>/<link> tags plus first-party-only, external <script src="…"> tags —
// for things like third-party site-verification meta tags, preconnect/icon links, or loading a
// trusted first-party script — with no way to smuggle in arbitrary inline JS. Every resource URL
// the tags carry (including a <script>'s src) must resolve to monday.com/mondaystaging.com (or be
// a relative path, which always resolves against whichever of those origins serves the page).
//
// Validated twice: at publish/update time (400 with the reasons) and again at render time, where a
// field that fails validation is dropped entirely. The render-time check is what actually protects
// the page — the Generator editor's Page Settings UI saves these fields without going through the
// /api/generated-pages/ endpoints, and values stored before a rule was tightened are never revalidated
// otherwise.
//
// Applied at render time (not baked into stored HTML), same as every other /gp/ tag injector here —
// see seo-tags.js/prefetch-tags.js — so a Page Settings edit takes effect immediately without
// republishing. The raw-passthrough render path (generated-controller.js) uses applyCustomCodeTags
// below; the chrome-added path (components/templates/ai-generated-template/) can't splice a raw
// HTML string into Next's <Head>, so it calls parseCustomCodeElements instead and renders real
// <meta>/<link>/<script> elements from the parsed result — both paths share this file so they can
// never diverge on what counts as valid.
const { parse } = require("node-html-parser");
const { insertBeforeClosingTag } = require("../../utils/html-utils");
const ALLOWED_TAGS = [
    "meta",
    "link",
    "script"
];
// Attribute-name allowlist per tag. Anything else is rejected — this is what keeps event handlers
// (onerror/onload/…) out, since the raw-passthrough path splices the stored string verbatim. No
// http-equiv: its only dangerous value (refresh) redirects the page, and verification tags use
// name/property.
const ALLOWED_ATTRIBUTES = {
    meta: [
        "name",
        "property",
        "content",
        "charset"
    ],
    link: [
        "rel",
        "href",
        "as",
        "type",
        "sizes",
        "media",
        "hreflang",
        "crossorigin",
        "integrity",
        "referrerpolicy"
    ],
    script: [
        "src",
        "async",
        "defer",
        "type",
        "crossorigin",
        "integrity",
        "referrerpolicy"
    ]
};
// Attributes the browser loads/navigates to. These are resolved exactly as the browser would (see
// findUnsafeResourceUrls) rather than pattern-matched, since "/\evil.com", "http:evil.com",
// " //evil.com" and "data:…" all look relative to a regex but resolve off-site or to inline code.
const RESOURCE_URL_ATTRIBUTES = [
    "href",
    "src"
];
const URL_RESOLUTION_BASE = "https://monday.com/";
// A <meta> tag with none of these is a no-op tag — nothing reads it, so treat it as an error rather
// than silently accepting dead markup.
const META_IDENTIFYING_ATTRS = [
    "name",
    "property",
    "charset"
];
const ELEMENT_NODE = 1;
const FIRST_PARTY_HOST_SUFFIXES = [
    "monday.com",
    "mondaystaging.com"
];
const isFirstPartyHost = (host)=>FIRST_PARTY_HOST_SUFFIXES.some((suffix)=>host === suffix || host.endsWith(`.${suffix}`));
// Matches an absolute URL ("https://…") or a protocol-relative one ("//host/…"). A bare path
// ("/x") or relative reference ("x/y") has no host of its own — it always resolves against
// whichever origin actually served the page (always monday.com/mondaystaging.com) — so those need
// no check at all.
const ABSOLUTE_URL_PATTERN = /^(https?:)?\/\//i;
const extractHost = (value)=>{
    try {
        return new URL(value.startsWith("//") ? `https:${value}` : value).hostname;
    } catch (e) {
        return null;
    }
};
const describeTag = (tagName, attributes)=>{
    const attrString = Object.entries(attributes).map(([key, value])=>`${key}="${value}"`).join(" ");
    return `<${tagName}${attrString ? ` ${attrString}` : ""}>`;
};
// href/src must resolve, the way the browser resolves them against the page, to an https URL on a
// first-party host. Relative paths resolve to monday.com and pass.
const resolveResourceUrl = (value)=>{
    try {
        return new URL(value, URL_RESOLUTION_BASE);
    } catch (e) {
        return null;
    }
};
const findUnsafeResourceUrls = (attributes = {})=>RESOURCE_URL_ATTRIBUTES.filter((attr)=>attr in attributes).reduce((offenders, attr)=>{
        const url = resolveResourceUrl(attributes[attr]);
        if (!url || url.protocol !== "https:" || !isFirstPartyHost(url.hostname)) {
            offenders.push({
                attr,
                value: attributes[attr],
                host: url && url.protocol === "https:" ? url.hostname : null
            });
        }
        return offenders;
    }, []);
// Non-resource attributes (e.g. a meta's content) are never loaded by the browser, so they only get
// the looser absolute-URL check — it keeps third-party URLs out of things like og:image.
const findThirdPartyUrls = (attributes = {})=>Object.entries(attributes).reduce((offenders, [attr, value])=>{
        if (RESOURCE_URL_ATTRIBUTES.includes(attr)) return offenders;
        if (typeof value !== "string" || !ABSOLUTE_URL_PATTERN.test(value)) return offenders;
        const host = extractHost(value);
        if (!host || !isFirstPartyHost(host)) offenders.push({
            attr,
            value,
            host
        });
        return offenders;
    }, []);
// node-html-parser preserves attribute-name case; HTML doesn't care, so "OnError" must not slip past
// the allowlist.
const lowercaseKeys = (attributes)=>Object.fromEntries(Object.entries(attributes).map(([key, value])=>[
            key.toLowerCase(),
            value
        ]));
// Parses a fragment of sibling <meta>/<link>/<script> tags (not a full document). Internal helper
// carries `rawText` too (needed to reject an inline <script> body — see validateCustomCodeHtml);
// parseCustomCodeElements (exported, used by the chrome-added render path) drops it since rendering
// only ever needs the tag name + attributes.
const parseCustomCodeNodes = (html)=>{
    if (!html) return [];
    return parse(html).childNodes.filter((node)=>node.nodeType === ELEMENT_NODE).map((node)=>({
            tagName: node.tagName.toLowerCase(),
            attributes: lowercaseKeys(node.attributes),
            rawText: node.rawText || ""
        }));
};
// Returns a list of human-readable problems; empty means the field is clean. `fieldName` is only
// used to make each message self-explanatory when headCustomCode and bodyCustomCode are both
// checked in the same request.
const validateCustomCodeHtml = (html, fieldName)=>{
    if (!html) return [];
    const errors = [];
    parseCustomCodeNodes(html).forEach(({ tagName, attributes, rawText })=>{
        if (!ALLOWED_TAGS.includes(tagName)) {
            errors.push(`'${fieldName}' contains a <${tagName}> tag — only ${ALLOWED_TAGS.map((t)=>`<${t}>`).join("/")} tags are allowed.`);
            return;
        }
        const disallowedAttrs = Object.keys(attributes).filter((attr)=>!ALLOWED_ATTRIBUTES[tagName].includes(attr));
        if (disallowedAttrs.length) {
            errors.push(`'${fieldName}' has a <${tagName}> tag with unsupported attribute(s) ${disallowedAttrs.join(", ")} — only ` + `${ALLOWED_ATTRIBUTES[tagName].join("/")} are allowed: ${describeTag(tagName, attributes)}`);
        }
        if (tagName === "meta" && !META_IDENTIFYING_ATTRS.some((attr)=>attr in attributes)) {
            errors.push(`'${fieldName}' has a <meta> tag with none of ${META_IDENTIFYING_ATTRS.join("/")} set: ${describeTag(tagName, attributes)}`);
        }
        if (tagName === "link" && !("rel" in attributes)) {
            errors.push(`'${fieldName}' has a <link> tag with no 'rel' attribute: ${describeTag(tagName, attributes)}`);
        }
        if (tagName === "script") {
            // No inline body allowed regardless of `src` — the whole point of this surface is that it
            // can never carry arbitrary executable JS, only a pointer to an already-trusted, first-party
            // script (the URL check below covers that `src` is actually first-party).
            if (!("src" in attributes)) {
                errors.push(`'${fieldName}' has a <script> tag with no 'src' — inline script bodies are not allowed, only external scripts: ${describeTag(tagName, attributes)}`);
            }
            if (rawText.trim()) {
                errors.push(`'${fieldName}' has a <script> tag with inline content — only external, first-party scripts (via 'src') are allowed: ${describeTag(tagName, attributes)}`);
            }
        }
        [
            ...findUnsafeResourceUrls(attributes),
            ...findThirdPartyUrls(attributes)
        ].forEach(({ attr, value, host })=>{
            errors.push(`'${fieldName}' references a non-first-party resource in ${describeTag(tagName, attributes)} — ${attr}="${value}" resolves to ` + `'${host || "a non-https or unparsable URL"}', which is not https on monday.com/mondaystaging.com.`);
        });
    });
    return errors;
};
// Render-time gate — see file header. A field that fails validation renders nothing.
const renderableCustomCode = (html)=>html && validateCustomCodeHtml(html, "render").length === 0 ? html : undefined;
// Chrome-added render path.
const parseCustomCodeElements = (html)=>parseCustomCodeNodes(renderableCustomCode(html)).map(({ tagName, attributes })=>({
            tagName,
            attributes
        }));
// Raw-passthrough render path only — see file header for why the chrome-added path can't reuse
// this. headCustomCode lands just before </head>; bodyCustomCode just before </body> (mirrors
// where aiBodyHtml itself sits — end-of-body, after the DOM it might reference has parsed).
const applyCustomCodeTags = (html, pageConfig = {})=>{
    const withHead = insertBeforeClosingTag(html, "</head>", renderableCustomCode(pageConfig.headCustomCode), (h, s)=>`${s}\n${h}`);
    return insertBeforeClosingTag(withHead, "</body>", renderableCustomCode(pageConfig.bodyCustomCode), (h, s)=>`${h}\n${s}`);
};
module.exports = {
    validateCustomCodeHtml,
    parseCustomCodeElements,
    applyCustomCodeTags,
    ALLOWED_TAGS,
    ALLOWED_ATTRIBUTES,
    META_IDENTIFYING_ATTRS
};
